if (!customElements.get('fbt-products')) {
  customElements.define(
    'fbt-products',
    class FrequentlyBoughtTogether extends HTMLElement {
      connectedCallback() {
        this.cards = this.querySelector('[data-fbt-cards]');
        this.badgeEl = this.querySelector('[data-fbt-badge]');
        this.originalRow = this.querySelector('[data-fbt-original-row]');
        this.originalEl = this.querySelector('[data-fbt-original]');
        this.totalEl = this.querySelector('[data-fbt-total]');
        this.submitEl = this.querySelector('[data-fbt-submit]');
        this.errorEl = this.querySelector('[data-fbt-error]');
        this.statusEl = this.querySelector('[data-fbt-status]');
        this.moneyFormat = this.dataset.moneyFormat || '{{amount}}';
        this.bundleDiscount = parseFloat(this.dataset.bundleDiscount) || 0;
        this.discountCode = (this.dataset.discountCode || '').trim();

        if (!this.cards || !this.submitEl) return;

        this.onChange = this.onChange.bind(this);
        this.onSubmit = this.onSubmit.bind(this);
        this.addEventListener('change', this.onChange);
        this.submitEl.addEventListener('click', this.onSubmit);

        this.loadRecommendations();
      }

      disconnectedCallback() {
        this.removeEventListener('change', this.onChange);
        if (this.submitEl) this.submitEl.removeEventListener('click', this.onSubmit);
      }

      /* Curated complementary products first, algorithmic related as a fallback so
         the section is useful before Search & Discovery is configured. */
      async loadRecommendations() {
        const added =
          (await this.fetchInto(this.dataset.urlComplementary)) ||
          (await this.fetchInto(this.dataset.urlRelated));

        // With nothing to pair, this is just the product again -- not worth showing.
        if (!added) {
          this.remove();
          return;
        }

        this.hidden = false;
        this.update();
      }

      async fetchInto(url) {
        if (!url) return false;

        try {
          const response = await fetch(url);
          if (!response.ok) throw new Error(`Request failed: ${response.status}`);

          const doc = new DOMParser().parseFromString(await response.text(), 'text/html');
          const fragment = doc.querySelector('[data-fbt-fragment]');
          if (!fragment || fragment.children.length === 0) return false;

          this.cards.append(...Array.from(fragment.children));
          return true;
        } catch (error) {
          console.error('[frequently-bought-together]', error);
          return false;
        }
      }

      onChange(event) {
        if (event.target.matches('[data-fbt-variant]')) this.syncCardPrice(event.target.closest('[data-fbt-card]'));
        this.update();
      }

      /* Keep the displayed price in step with the chosen variant. */
      syncCardPrice(card) {
        if (!card) return;
        const option = card.querySelector('select[data-fbt-variant]')?.selectedOptions?.[0];
        if (!option) return;

        const price = parseInt(option.dataset.price, 10) || 0;
        const compareAt = parseInt(option.dataset.compareAt, 10) || 0;

        const current = card.querySelector('[data-fbt-price-current]');
        if (current) current.textContent = this.formatMoney(price);

        const was = card.querySelector('[data-fbt-price-was]');
        if (was) {
          was.textContent = this.formatMoney(compareAt);
          was.hidden = compareAt <= price;
        }
      }

      selected() {
        return Array.from(this.cards.querySelectorAll('[data-fbt-card]')).filter((card) => {
          const checkbox = card.querySelector('[data-fbt-checkbox]');
          return checkbox && checkbox.checked && !checkbox.disabled;
        });
      }

      update() {
        const cards = Array.from(this.cards.querySelectorAll('[data-fbt-card]'));

        // Dim anything unticked so the strip reads at a glance.
        cards.forEach((card) => {
          const checkbox = card.querySelector('[data-fbt-checkbox]');
          card.classList.toggle('fbt__card--off', !(checkbox && checkbox.checked));
        });

        const chosen = this.selected();
        const prices = chosen.map((card) => this.cardPrice(card));
        const original = prices.reduce((sum, price) => sum + price, 0);

        /* Shopify allocates a percentage discount line by line and rounds each
           line, so mirror that here rather than discounting the sum. */
        const discounting = this.bundleDiscount > 0 && chosen.length > 1;
        const total = discounting
          ? prices.reduce((sum, price) => sum + (price - Math.round((price * this.bundleDiscount) / 100)), 0)
          : original;
        const savings = original - total;

        if (this.totalEl) this.totalEl.textContent = this.formatMoney(total);

        if (this.originalRow) this.originalRow.hidden = savings <= 0;
        if (this.originalEl) this.originalEl.textContent = this.formatMoney(original);

        if (this.badgeEl) {
          this.badgeEl.hidden = savings <= 0;
          this.badgeEl.textContent = savings > 0 ? `Save ${this.formatMoney(savings)}` : '';
        }

        this.submitEl.disabled = chosen.length === 0;
      }

      cardPrice(card) {
        const field = card.querySelector('[data-fbt-variant]');
        if (!field) return 0;
        const source = field.tagName === 'SELECT' ? field.selectedOptions[0] : field;
        return parseInt(source?.dataset.price, 10) || 0;
      }

      async onSubmit() {
        const chosen = this.selected();
        if (chosen.length === 0) return;

        const items = chosen.map((card) => ({
          id: Number(card.querySelector('[data-fbt-variant]').value),
          quantity: 1,
        }));

        this.submitEl.classList.add('loading');
        this.submitEl.disabled = true;
        this.hideError();

        try {
          const response = await fetch(`${routes.cart_add_url}.js`, {
            method: 'POST',
            headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
            body: JSON.stringify({ items }),
          });

          const data = await response.json();
          if (!response.ok) throw new Error(data.description || data.message || 'Could not add to cart');

          if (this.discountCode && items.length > 1) await this.applyDiscount();

          // Mirrors how this theme's product form hands off to Shopflo after adding.
          const cart = await fetch(`${routes.cart_url}.js`).then((res) => res.json());
          if (typeof handleFloCartBtn === 'function') handleFloCartBtn();

          if (typeof publish === 'function' && typeof PUB_SUB_EVENTS !== 'undefined') {
            publish(PUB_SUB_EVENTS.cartUpdate, {
              source: 'frequently-bought-together',
              cartData: cart,
            });
          }

          if (this.statusEl) {
            this.statusEl.textContent = `${items.length} item${items.length === 1 ? '' : 's'} added to your cart`;
          }
        } catch (error) {
          console.error('[frequently-bought-together]', error);
          this.showError(error.message);
        } finally {
          this.submitEl.classList.remove('loading');
          this.update();
        }
      }

      /* Puts the code in the session so it is waiting at checkout. Redirecting to
         cart.js keeps the follow-up response small. A failure here must not lose
         the items already added, so it only warns. */
      async applyDiscount() {
        try {
          await fetch(`/discount/${encodeURIComponent(this.discountCode)}?redirect=/cart.js`, {
            credentials: 'same-origin',
          });
        } catch (error) {
          console.warn('[frequently-bought-together] discount not applied', error);
        }
      }

      showError(message) {
        if (!this.errorEl) return;
        this.errorEl.textContent = message || 'Something went wrong. Please try again.';
        this.errorEl.hidden = false;
      }

      hideError() {
        if (this.errorEl) this.errorEl.hidden = true;
      }

      /* Minimal Shopify money formatter so the total matches the per-item prices
         Liquid already rendered, including the store's comma grouping. */
      formatMoney(cents) {
        const value = typeof cents === 'number' ? cents : 0;

        const withCommas = (number) => {
          const parts = number.split('.');
          parts[0] = parts[0].replace(/(\d)(?=(\d{3})+$)/g, '$1,');
          return parts.join('.');
        };

        const indian = (number) => {
          const parts = number.split('.');
          const digits = parts[0];
          const last3 = digits.slice(-3);
          const rest = digits.slice(0, -3);
          parts[0] = rest ? `${rest.replace(/(\d)(?=(\d\d)+$)/g, '$1,')},${last3}` : last3;
          return parts.join('.');
        };

        const group = this.moneyFormat.includes('amount_no_decimals_with_comma_separator') ||
          this.dataset.currency === 'INR' ||
          /₹/.test(this.moneyFormat)
          ? indian
          : withCommas;

        return this.moneyFormat.replace(/\{\{\s*(\w+)\s*\}\}/g, (_match, token) => {
          switch (token) {
            case 'amount':
              return group((value / 100).toFixed(2));
            case 'amount_no_decimals':
              return group(Math.round(value / 100).toString());
            case 'amount_with_comma_separator':
              return group((value / 100).toFixed(2)).replace(/,/g, ' ').replace('.', ',');
            case 'amount_no_decimals_with_comma_separator':
              return group(Math.round(value / 100).toString());
            case 'amount_with_apostrophe_separator':
              return group((value / 100).toFixed(2)).replace(/,/g, "'");
            default:
              return group((value / 100).toFixed(2));
          }
        });
      }
    }
  );
}
